import React, { useState, useEffect } from "react";
import { Drawer, message, Empty, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useAISidebarSearch } from "./customHooks/useAISidebarSearch";
import { SearchOutlined } from "@ant-design/icons";
import { detectAILeaveAction } from "./leaveActionHandler";

import dayjs from "dayjs";
import { getAIChatResponse } from "./aiSmallTalk";
import { detectDashboardIntent } from "./detectDashboardIntent";
import { Mic } from "react-feather";
import { detectDepartmentIntent } from "./department/detectDepartmentIntent";
import { processPendingDeleteDepartment, processPendingDepartment, startCreateDepartmentFlow, startDeleteDepartmentFlow } from "./department/aiDepartmentCreateHandler";
import { processPendingProfileUpdate, startProfileUpdateFlow } from "./profile/aiProfileUpdateHandler";
import { detectProfileIntent } from "./profile/detectProfileIntent";

interface ChatMessage {
    from: "user" | "ai";
    text: string;
}

const speak = (text: string) => {
    if (!window.speechSynthesis) return;

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "en-US";
    utter.rate = 1;      // speed
    utter.pitch = 1;     // tone
    utter.volume = 1;    // volume
    window.speechSynthesis.speak(utter);
};


const AISearchDrawer = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [chat, setChat] = useState<ChatMessage[]>([]);
    const [pendingDept, setPendingDept] = useState<any | null>(null);
    const [pendingDeleteDept, setPendingDeleteDept] = useState<any | null>(null);
    const [pendingProfile, setPendingProfile] = useState<any | null>(null);


    const pushUserChat = (msg: string) =>
        setChat(prev => [...prev, { from: "user", text: msg }]);

    const pushAIChat = (msg: string) => {
        setChat(prev => [...prev, { from: "ai", text: msg }]);

        if (isRecording) {
            speak(msg);
        }
    };

    const [typeSuggestions, setTypeSuggestions] = useState<any[]>([]);
    const [dateSuggestions, setDateSuggestions] = useState<any>(null);
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [aiLoading, setAiLoading] = useState(false);

    const navigate = useNavigate();
    const { suggestions, updateSuggestionsList, searchMenus, loading } = useAISidebarSearch();


    // 🎤 Voice Recognition Setup
    const [isRecording, setIsRecording] = useState(false);
    let recognition: any = null;

    if ("webkitSpeechRecognition" in window) {
        const SpeechRecognition = (window as any).webkitSpeechRecognition;
        recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = "en-US";

        recognition.onresult = (event: any) => {
            const voiceText = event.results[0][0].transcript;
            setQuery(voiceText); // put text into input
            pushUserChat(voiceText); // add to chat
            const cleaned = voiceText.toLowerCase();
            handleAIFromVoice(cleaned);
        };

        recognition.onerror = (err: any) => {
            console.error("Speech Error:", err);
            setIsRecording(false);
        };

        recognition.onend = () => {
            setIsRecording(false);
        };
    }

    useEffect(() => {
        const handler = () => {
            document.getElementById("ai-global-upload")?.click();
        };
        window.addEventListener("ai-open-file-upload", handler);
        return () => window.removeEventListener("ai-open-file-upload", handler);
    }, []);

    // 🎤 Voice Handler
    const startVoiceInput = () => {
        if (!recognition) {
            message.error("Voice recognition not supported in this browser.");
            return;
        }
        setIsRecording(true);
        recognition.start();
    };

    // When AI should process voice text
    const handleAIFromVoice = (cleaned: string) => {
        // AI SMALL TALK
        const smallTalk = getAIChatResponse(cleaned);
        if (smallTalk) {
            pushAIChat(smallTalk);   // already speaks
            return;
        }

        const action = detectAILeaveAction(cleaned);
        if (action) {
            window.dispatchEvent(new CustomEvent("ai-action", { detail: action }));
            speak("Okay, processing your leave request.");   // optional
            return;
        }

        const profileIntent = detectProfileIntent(cleaned);

        if (profileIntent?.intent === "update_photo") {
            startProfileUpdateFlow("photo", setPendingProfile, pushAIChat, speak);
            return;
        }
        if (profileIntent?.intent === "update_name") {
            startProfileUpdateFlow("name", setPendingProfile, pushAIChat, speak);
            return;
        }

        if (profileIntent?.intent === "update_mobile") {
            startProfileUpdateFlow("mobile", setPendingProfile, pushAIChat, speak);
            return;
        }
        if (pendingProfile) {
            processPendingProfileUpdate(cleaned, pendingProfile, setPendingProfile, pushAIChat, speak);
            return;
        }

        const dashboardIntent = detectDashboardIntent(cleaned);
        if (dashboardIntent) {
            if (dashboardIntent.intent === "export_report") {
                speak("Preparing your export file");
                pushAIChat("📦 Preparing your export file...");
                window.dispatchEvent(
                    new CustomEvent("ai-export-trigger", { detail: dashboardIntent })
                );
                return;
            }

            speak("Opening dashboard");
            pushAIChat("📊 Loading dashboard data...");
            navigate("/ai-dashboard", { state: { dashboardIntent } });
            return;
        }

        const deptIntent = detectDepartmentIntent(cleaned);

        if (deptIntent?.intent === "create_department") {
            startCreateDepartmentFlow(
                setPendingDept,
                pushAIChat,
                speak,
                deptIntent.name
            );
            return;
        }

        if (pendingDept) {
            processPendingDepartment(
                cleaned,
                pendingDept,
                setPendingDept,
                pushAIChat,
                speak
            );
            return;
        }
        pushAIChat("😅 I'm still learning! Say it again?");
    };

    useEffect(() => {
        const startHandler = () => setAiLoading(true);
        const stopHandler = () => setAiLoading(false);

        window.addEventListener("ai-loader-start", startHandler);
        window.addEventListener("ai-loader-stop", stopHandler);

        return () => {
            window.removeEventListener("ai-loader-start", startHandler);
            window.removeEventListener("ai-loader-stop", stopHandler);
        };
    }, []);

    useEffect(() => {
        const handler = (event: any) => {
            pushAIChat(event.detail);
        };
        window.addEventListener("ai-chat", handler);
        return () => window.removeEventListener("ai-chat", handler);
    }, []);

    useEffect(() => {
        const handler = (event: any) => {
            setTypeSuggestions(event.detail || []);
        };
        window.addEventListener("ai-suggest-types", handler);
        return () => window.removeEventListener("ai-suggest-types", handler);
    }, []);

    useEffect(() => {
        const handler = (event: any) => {
            setDateSuggestions(event.detail);
        };
        window.addEventListener("ai-suggest-dates", handler);
        return () => window.removeEventListener("ai-suggest-dates", handler);
    }, []);

    // Open drawer via global event (Cmd+K / Ctrl+K style)
    useEffect(() => {
        const handler = () => {
            setOpen(true);
            setQuery("");
            updateSuggestionsList("");
        };
        window.addEventListener("open-ai-search", handler);
        return () => window.removeEventListener("open-ai-search", handler);
    }, [updateSuggestionsList]);

    const handleAISearch = () => {
        const cleaned = query.trim().toLowerCase();
        // 👇 user's text should appear immediately
        pushUserChat(query);
        setQuery("");
        // 1️⃣ SMALL TALK / GREETINGS
        const smallTalk = getAIChatResponse(cleaned);
        if (smallTalk) {
            pushAIChat(smallTalk);
            return;
        }

        // 2️⃣ LEAVE LOGIC
        const action = detectAILeaveAction(cleaned);

        const profileIntent = detectProfileIntent(cleaned);

        if (profileIntent?.intent === "update_photo") {
            startProfileUpdateFlow("photo", setPendingProfile, pushAIChat, speak);
            return;
        }

        if (action) {
            window.dispatchEvent(new CustomEvent("ai-action", { detail: action }));
            return;
        }

        const dashboardIntent = detectDashboardIntent(cleaned);

        if (dashboardIntent) {
            if (dashboardIntent.intent === "export_report") {
                window.dispatchEvent(
                    new CustomEvent("ai-export-trigger", { detail: dashboardIntent })
                );
                pushAIChat("📦 Preparing your export file...");
                return;
            }

            pushAIChat("📊 Loading dashboard data...");
            navigate("/ai-dashboard", { state: { dashboardIntent } });
            return;
        }

        const deptIntent = detectDepartmentIntent(cleaned);

        if (deptIntent?.intent === "create_department") {
            startCreateDepartmentFlow(
                setPendingDept,
                pushAIChat,
                speak,
                deptIntent.name
            );
            return;
        }
        if (pendingDept) {
            processPendingDepartment(
                cleaned,
                pendingDept,
                setPendingDept,
                pushAIChat,
                speak
            );
            return;
        }
        // DELETE department intent
        if (deptIntent?.intent === "delete_department") {

            startDeleteDepartmentFlow(
                setPendingDeleteDept,
                pushAIChat,
                speak,
                deptIntent.name
            );
            return;
        }
        // Continue delete flow
        if (pendingDeleteDept) {
            processPendingDeleteDepartment(
                cleaned,
                pendingDeleteDept,
                setPendingDeleteDept,
                pushAIChat,
                speak
            );
            return;
        }

        // PROFILE INTENT

        // if (profileIntent?.intent === "open_profile") {
        //     pushAIChat("Opening your profile...");
        //     speak("Opening your profile");
        //     navigate("/pages/profile");
        //     return;
        // }
        if (profileIntent?.intent === "update_name") {
            startProfileUpdateFlow("name", setPendingProfile, pushAIChat, speak);
            return;
        }

        if (profileIntent?.intent === "update_mobile") {
            startProfileUpdateFlow("mobile", setPendingProfile, pushAIChat, speak);
            return;
        }

        if (pendingProfile) {
            processPendingProfileUpdate(cleaned, pendingProfile, setPendingProfile, pushAIChat, speak);
            return;
        }


        // 3️⃣ FUNNY FALLBACK
        pushAIChat("😅 I'm still learning! I don't know this yet — but every day I'm levelling up like a Pokémon!");
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleAISearch();
        }
    };

    // Unicode sparkle for the AI magic ✨ (or use an AntD icon below)
    const SparkleIcon = () => <span style={{ fontSize: "inherit" }}>✨</span>;

    return (
        <Drawer
            style={{ marginTop: 50, paddingBottom: 50 }}
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <SparkleIcon />
                    <strong>Leadchain AI</strong>
                </div>
            }
            placement="right"
            width={420}
            open={open}
            onClose={() => setOpen(false)}
            closeIcon={null}
            styles={{
                body: { padding: 0, display: "flex", flexDirection: "column", height: "100%" },
                header: { borderBottom: "1px solid #f0f0f0", padding: "12px 20px" },
            }}
        >
            {/* Suggestions Area - Chat Style */}
            {/* Chat + Suggestions Area */}
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "20px",
                    background: "#fafafa",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                }}
            >
                {/* 🔥 AI CHAT MESSAGES */}
                {chat.map((msg, index) => (
                    <div
                        key={index}
                        style={{
                            display: "flex",
                            justifyContent: msg.from === "user" ? "flex-end" : "flex-start"
                        }}
                    >
                        <div
                            style={{
                                padding: "10px 14px",
                                background: msg.from === "user" ? "#722ed1" : "#eee",
                                color: msg.from === "user" ? "white" : "black",
                                borderRadius: 12,
                                maxWidth: "75%",
                            }}
                        >
                            {msg.text}
                        </div>
                    </div>
                ))}

                {/* 🔥 AI SEARCH SUGGESTIONS */}
                {loading ? (
                    <div style={{ textAlign: "center", padding: "40px 0" }}>
                        <Spin size="large" />
                    </div>
                ) : suggestions.length === 0 && query ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <span style={{ color: "#999" }}>
                                No results for "<i>{query}</i>"
                            </span>
                        }
                    />
                ) : suggestions.length === 0 ? (
                    <div style={{ textAlign: "center", marginTop: 60, color: "#999" }}>
                        Ask me anything — Payroll, Attendance, Leave…
                    </div>
                ) : (
                    suggestions.map((item: any, index: number) => (
                        <div
                            key={index}
                            onClick={() => {
                                navigate(item.link);
                                setOpen(false);
                            }}
                            style={{
                                padding: "14px",
                                background: "white",
                                borderRadius: 12,
                                cursor: "pointer",
                                border: "1px solid #eee",
                            }}
                        >
                            <b>{item.label}</b>
                        </div>
                    ))
                )}

                {/* 🔥 Leave Type Suggestions */}
                {typeSuggestions.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {typeSuggestions.map((l: any, idx: number) => (
                            <div
                                key={idx}
                                onClick={() => {
                                    setTypeSuggestions([]);
                                    setSelectedType(l.LeaveTypeName);

                                    const newQuery = selectedDate
                                        ? `apply ${l.LeaveTypeName.toLowerCase()} leave on ${selectedDate}`
                                        : `apply ${l.LeaveTypeName.toLowerCase()}`;

                                    setQuery(newQuery);
                                }}

                                style={{
                                    padding: "6px 12px",
                                    background: "#e6f4ff",
                                    borderRadius: 20,
                                    cursor: "pointer",
                                    border: "1px solid #91caff",
                                    fontSize: 13
                                }}
                            >
                                {l.LeaveTypeName}
                            </div>
                        ))}
                    </div>
                )}
                {/* 🔥 Date Suggestions */}
                {dateSuggestions && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                        {Object.entries(dateSuggestions).map(([key, date], index) => (
                            <div
                                key={index}
                                onClick={() => {
                                    const prettyDate = dayjs(date as string).format("DD MMM");

                                    setSelectedDate(prettyDate);
                                    setDateSuggestions(null);

                                    // Merge leave type + date
                                    const newQuery = selectedType
                                        ? `apply ${selectedType.toLowerCase()} on ${prettyDate}`
                                        : `apply leave on ${prettyDate}`;

                                    setQuery(newQuery);
                                }}

                                style={{
                                    padding: "6px 12px",
                                    background: "#fff7e6",
                                    borderRadius: 20,
                                    cursor: "pointer",
                                    border: "1px solid #ffd591",
                                    fontSize: 13
                                }}
                            >
                                {key === "today" && "Today"}
                                {key === "tomorrow" && "Tomorrow"}
                                {key === "dayAfter" && "Day After Tomorrow"}
                                {key === "nextWeek" && "Next Week"}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Search Input Bar */}
            <div
                style={{
                    padding: "16px 20px",
                    borderTop: "1px solid #f0f0f0",
                    background: "white",
                    boxShadow: "0 -4px 12px rgba(0,0,0,0.05)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        background: "#f5f5f5",
                        borderRadius: 12,
                        padding: "12px 16px",
                        border: "1px solid #e8e8e8",
                        transition: "all 0.2s",
                    }}
                    onFocus={(e) => {
                        e.currentTarget.style.borderColor = "#722ed1";
                        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(114, 46, 209, 0.15)";
                    }}
                    onBlur={(e) => {
                        e.currentTarget.style.borderColor = "#e8e8e8";
                        e.currentTarget.style.boxShadow = "none";
                    }}
                >
                    <SearchOutlined style={{ color: "#999", fontSize: 18, marginRight: 12 }} />
                    {/* 🌐 Global Upload Button */}
                    <input
                        type="file"
                        id="ai-global-upload"
                        accept="image/*,.pdf,.jpg,.jpeg,.png"
                        style={{ display: "none" }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            pushAIChat(`📄 File received: ${file.name}`);

                            // 🔥 Send file to GlobalMasterListener
                            window.dispatchEvent(
                                new CustomEvent("ai-file-upload", {
                                    detail: { file },
                                })
                            );
                        }}
                    />

                    <input
                        type="text"
                        value={query}
                        autoFocus
                        onChange={(e) => {
                            setQuery(e.target.value);
                            updateSuggestionsList(e.target.value);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Search menus or ask to perform actions..."
                        style={{
                            flex: 1,
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            fontSize: 15,
                        }}
                    />
                    <div
                        onClick={startVoiceInput}
                        style={{
                            marginRight: 10,
                            marginBottom: 8,
                            cursor: "pointer",
                            color: isRecording ? "red" : "#555",
                            fontSize: 20
                        }}
                        title="Speak your command"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-label=""
                            className="icon"
                            fontSize="inherit"
                        >
                            <path d="M15.7806 10.1963C16.1326 10.3011 16.3336 10.6714 16.2288 11.0234L16.1487 11.2725C15.3429 13.6262 13.2236 15.3697 10.6644 15.6299L10.6653 16.835H12.0833L12.2171 16.8486C12.5202 16.9106 12.7484 17.1786 12.7484 17.5C12.7484 17.8214 12.5202 18.0894 12.2171 18.1514L12.0833 18.165H7.91632C7.5492 18.1649 7.25128 17.8672 7.25128 17.5C7.25128 17.1328 7.5492 16.8351 7.91632 16.835H9.33527L9.33429 15.6299C6.775 15.3697 4.6558 13.6262 3.84992 11.2725L3.76984 11.0234L3.74445 10.8906C3.71751 10.5825 3.91011 10.2879 4.21808 10.1963C4.52615 10.1047 4.84769 10.2466 4.99347 10.5195L5.04523 10.6436L5.10871 10.8418C5.8047 12.8745 7.73211 14.335 9.99933 14.335C12.3396 14.3349 14.3179 12.7789 14.9534 10.6436L15.0052 10.5195C15.151 10.2466 15.4725 10.1046 15.7806 10.1963ZM12.2513 5.41699C12.2513 4.17354 11.2437 3.16521 10.0003 3.16504C8.75675 3.16504 7.74835 4.17343 7.74835 5.41699V9.16699C7.74853 10.4104 8.75685 11.418 10.0003 11.418C11.2436 11.4178 12.2511 10.4103 12.2513 9.16699V5.41699ZM13.5814 9.16699C13.5812 11.1448 11.9781 12.7479 10.0003 12.748C8.02232 12.748 6.41845 11.1449 6.41828 9.16699V5.41699C6.41828 3.43889 8.02221 1.83496 10.0003 1.83496C11.9783 1.83514 13.5814 3.439 13.5814 5.41699V9.16699Z" />
                        </svg>
                    </div>

                    <div
                        style={{
                            background: query ? "#722ed1" : "#ddd",
                            color: "white",
                            padding: "4px 6px",
                            borderRadius: 6,
                            fontSize: 13,
                            fontWeight: 600,
                            transition: "background 0.2s",
                        }}
                        onClick={handleAISearch}
                    >
                        {/* ⏎ */}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="white"
                        >
                            <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
                        </svg>
                    </div>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: "#aaa", textAlign: "center" }}>
                    Powered by Leadchain AI • Try: "Menu Name " or "Apply Leave"
                </div>
            </div>
            {aiLoading && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "rgba(255,255,255,0.8)",
                        backdropFilter: "blur(3px)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,

                    }}
                >
                    <Spin size="large" tip="Processing..." />
                </div>
            )}
            {isRecording && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(168, 85, 247, 0.95) 100%)",
                        backdropFilter: "blur(12px)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 999999,
                        animation: "fadeIn 0.3s ease",
                        marginTop: 50,
                    }}
                >
                    {/* Animated Wave Circles */}
                    <div style={{ position: "relative", width: 120, height: 120 }}>
                        <div className="wave-circle wave-1"></div>
                        <div className="wave-circle wave-2"></div>
                        <div className="wave-circle wave-3"></div>

                        {/* Microphone Icon Container */}
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: 70,
                                height: 70,
                                borderRadius: "50%",
                                background: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                                animation: "pulse 2s ease-in-out infinite",
                            }}
                        >
                            <Mic size={32} color="#6366f1" strokeWidth={2.5} />
                        </div>
                    </div>

                    {/* Listening Text */}
                    <div
                        style={{
                            marginTop: 40,
                            fontSize: 24,
                            fontWeight: 600,
                            color: "white",
                            letterSpacing: "0.5px",
                        }}
                    >
                        Listening<span className="dots">...</span>
                    </div>

                    {/* Subtitle */}
                    <div
                        style={{
                            marginTop: 12,
                            fontSize: 15,
                            color: "rgba(255, 255, 255, 0.8)",
                            fontWeight: 400,
                        }}
                    >
                        Speak your command clearly
                    </div>

                    {/* Waveform Visualization */}
                    <div
                        style={{
                            display: "flex",
                            gap: 4,
                            marginTop: 30,
                            alignItems: "center",
                            height: 40,
                        }}
                    >
                        {[...Array(5)].map((_, i) => (
                            <div
                                key={i}
                                className="wave-bar"
                                style={{
                                    width: 4,
                                    background: "white",
                                    borderRadius: 2,
                                    animationDelay: `${i * 0.1}s`,
                                }}
                            ></div>
                        ))}
                    </div>
                </div>
            )}
        </Drawer>
    );
};

export default AISearchDrawer;